import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const SupportPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const services = [
    {
      icon: 'ri-file-list-3-line',
      title: '簡易宿所申請サポート',
      description: '保健所への申請書類作成から提出まで、実務経験に基づいた確実なサポートを提供します。'
    },
    {
      icon: 'ri-home-4-line',
      title: '民泊（住宅宿泊事業）届出サポート',
      description: '住宅宿泊事業法に基づく届出手続きを、スムーズに完了できるようサポートします。'
    },
    {
      icon: 'ri-hospital-line',
      title: '保健所対応アドバイス',
      description: '保健所との折衝や指摘事項への対応方法を、実例をもとにアドバイスします。'
    },
    {
      icon: 'ri-fire-line',
      title: '消防対応アドバイス',
      description: '消防設備の設置基準や検査対応について、具体的な指導を行います。'
    },
    {
      icon: 'ri-map-pin-line',
      title: '物件選定アドバイス',
      description: '立地条件や法規制を考慮した、収益性の高い物件選びをサポートします。'
    },
    {
      icon: 'ri-rocket-line',
      title: '運営立ち上げ支援',
      description: '清掃業者の選定から価格設定まで、運営開始に必要な準備を総合的に支援します。'
    },
    {
      icon: 'ri-airplay-line',
      title: 'Airbnbページ作成サポート',
      description: '予約率を高める魅力的なリスティング作成と、効果的な写真撮影のアドバイスを提供します。'
    },
    {
      icon: 'ri-file-text-line',
      title: '確定申告サポート（宿泊業向け）',
      description: '宿泊業特有の経費処理や青色申告の方法を、分かりやすく指導します。'
    },
    {
      icon: 'ri-calculator-line',
      title: '税務相談サポート',
      description: '消費税や固定資産税など、民泊運営に関わる税務全般の相談に対応します。'
    }
  ];

  const pricing = [
    {
      service: '簡易宿所申請サポート',
      price: '150,000円〜',
      note: '物件規模により変動'
    },
    {
      service: '民泊届出サポート',
      price: '80,000円〜',
      note: '書類作成・提出代行含む'
    },
    {
      service: '保健所・消防対応アドバイス',
      price: '30,000円〜',
      note: '1回あたり'
    },
    {
      service: '物件選定アドバイス',
      price: '50,000円〜',
      note: '現地調査含む'
    },
    {
      service: '運営立ち上げ支援',
      price: '100,000円〜',
      note: '3ヶ月間のサポート'
    },
    {
      service: 'Airbnbページ作成サポート',
      price: '40,000円〜',
      note: '写真撮影別途'
    },
    {
      service: '確定申告サポート',
      price: '60,000円〜',
      note: '年間サポート'
    },
    {
      service: '税務相談サポート',
      price: '20,000円〜',
      note: '1回あたり'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'お問い合わせ',
      description: 'フォームまたはお電話でお気軽にご相談ください。初回相談は無料です。'
    },
    {
      number: '02',
      title: 'ヒアリング',
      description: 'ご希望の開業形態や物件状況を詳しくお伺いし、最適なプランをご提案します。'
    },
    {
      number: '03',
      title: 'サポート開始',
      description: '申請書類の作成から提出、各種手続きまで、段階的にサポートを進めます。'
    },
    {
      number: '04',
      title: '収益化',
      description: '営業許可取得後も、運営開始から安定収益化まで継続的に伴走支援します。'
    }
  ];

  const faqs = [
    {
      question: '民泊を始めるには何が必要ですか？',
      answer: '民泊を始めるには、主に「簡易宿所営業許可」または「住宅宿泊事業届出」のいずれかが必要です。物件の用途地域や建物の種類によって適用される法律が異なります。また、消防設備の設置や保健所の検査も必要となります。'
    },
    {
      question: '申請から営業開始までどのくらいかかりますか？',
      answer: '簡易宿所の場合、申請から許可取得まで通常2〜3ヶ月程度かかります。住宅宿泊事業の場合は届出から約2週間で営業可能です。ただし、物件の状況や自治体によって期間は変動します。'
    },
    {
      question: '費用はどのくらいかかりますか？',
      answer: '申請手数料や消防設備の設置費用を含めると、簡易宿所で50万円〜150万円程度、住宅宿泊事業で20万円〜50万円程度が目安です。物件の状況により大きく変動するため、まずは無料相談でお見積りをご提案します。'
    },
    {
      question: 'マンションでも民泊運営はできますか？',
      answer: 'マンションの管理規約で民泊が禁止されていなければ可能です。ただし、用途地域の制限や消防設備の設置基準を満たす必要があります。事前に管理組合への確認が必須です。'
    },
    {
      question: '副業でも民泊運営はできますか？',
      answer: 'はい、可能です。多くの方が会社員として働きながら副業で民泊を運営されています。清掃や鍵の受け渡しを代行業者に委託することで、本業に支障なく運営できます。'
    },
    {
      question: 'サポート範囲外の地域でも相談できますか？',
      answer: '東京エリアを中心にサポートしていますが、他地域の方もまずはご相談ください。オンラインでのアドバイスや書類作成サポートなど、可能な範囲でお手伝いさせていただきます。'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6h4au35pk3jcsh37h10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const title = t('seo.support.title');
    const description = t('seo.support.description');

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', t('seo.support.keywords'));
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}/minpaku-license-support`);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}/minpaku-license-support`);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    const schemaId = 'schema-support';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: title,
      description: description,
      url: `${SITE_URL}/minpaku-license-support`,
      provider: {
        '@type': 'LocalBusiness',
        name: 'Magome Kominka',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Ota City',
          addressRegion: 'Tokyo',
          addressCountry: 'JP',
        },
      },
      areaServed: { '@type': 'City', name: 'Tokyo' },
      serviceType: ['Minpaku Registration', 'Ryokan License Application', 'Airbnb Launch Support'],
      offers: {
        '@type': 'Offer',
        priceCurrency: 'JPY',
        availability: 'https://schema.org/InStock',
      },
    });

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [i18n.language, t]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-32">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/7aa368e378087cef877e428eb6c721fe.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              民泊・簡易宿所<br />開業サポート
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
              現役ホストが実務ベースでサポート<br />
              申請から収益化まで、安心の伴走支援
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm md:text-base">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <i className="ri-check-line text-emerald-400 text-xl"></i>
                <span>東京エリア実績多数</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <i className="ri-check-line text-emerald-400 text-xl"></i>
                <span>初回相談無料</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <i className="ri-check-line text-emerald-400 text-xl"></i>
                <span>収益化まで完全サポート</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">なぜ私たちが選ばれるのか</h2>
              <p className="text-lg text-slate-600">実務経験に基づいた確実なサポートで、あなたの開業を成功に導きます</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-user-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">現役ホストが直接サポート</h3>
                <p className="text-slate-600 leading-relaxed">
                  実際に民泊を運営している現役ホストが、実務で培ったノウハウを直接お伝えします。机上の理論ではなく、現場で使える知識を提供します。
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-map-pin-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">東京エリア実績あり</h3>
                <p className="text-slate-600 leading-relaxed">
                  東京都内での申請実績が豊富です。各区の保健所や消防署の特性を熟知しており、スムーズな手続きをサポートします。
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-line-chart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">収益化まで伴走支援</h3>
                <p className="text-slate-600 leading-relaxed">
                  許可取得後も、運営開始から安定した収益を上げるまで継続的にサポート。価格設定や集客方法もアドバイスします。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">サポート内容</h2>
              <p className="text-lg text-slate-600">開業に必要なすべての手続きを、ワンストップでサポートします</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-slate-50 p-6 rounded-xl hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="w-14 h-14 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">料金目安</h2>
              <p className="text-lg text-slate-600">明確な料金体系で、安心してご依頼いただけます</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="px-6 py-4 text-left font-semibold">サービス内容</th>
                      <th className="px-6 py-4 text-left font-semibold">料金</th>
                      <th className="px-6 py-4 text-left font-semibold">備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricing.map((item, index) => (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-slate-900 font-medium">{item.service}</td>
                        <td className="px-6 py-4 text-slate-900 font-bold">{item.price}</td>
                        <td className="px-6 py-4 text-slate-600 text-sm">{item.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex gap-3">
                <i className="ri-information-line text-amber-600 text-xl flex-shrink-0 mt-1"></i>
                <div>
                  <p className="text-slate-700 leading-relaxed">
                    <strong className="text-slate-900">料金は目安です。</strong>物件の状況や申請内容により変動します。まずは無料相談で詳細なお見積りをご提案させていただきます。複数サービスをまとめてご依頼いただく場合は、割引価格でご提供可能です。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">サポートの流れ</h2>
              <p className="text-lg text-slate-600">お問い合わせから収益化まで、4つのステップで進めます</p>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-slate-50 p-6 rounded-xl hover:shadow-lg transition-shadow">
                    <div className="text-5xl font-bold text-slate-200 mb-4">{step.number}</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-3xl text-slate-300"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">よくある質問</h2>
              <p className="text-lg text-slate-600">民泊開業に関するよくあるご質問にお答えします</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-2xl text-slate-400 flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-5">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cross-link Banner → 運営代行ページへ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">

            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-bold tracking-widest uppercase px-5 py-2.5 rounded-full">
                <i className="ri-arrow-right-circle-line text-base"></i>
                開業後のステップ
              </span>
            </div>

            <div className="relative overflow-hidden rounded-3xl shadow-[0_20px_80px_rgba(0,0,0,0.18)]">
              {/* Top accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 via-yellow-300 to-amber-500 z-20" />

              {/* Background image */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url('https://readdy.ai/api/search-image?query=Luxury%20Tokyo%20apartment%20interior%20at%20golden%20hour%2C%20warm%20amber%20and%20dark%20tones%2C%20modern%20minimalist%20design%2C%20city%20skyline%20visible%20through%20large%20windows%2C%20premium%20hospitality%20property%20management%2C%20sophisticated%20living%20space%20with%20elegant%20furniture%2C%20professional%20real%20estate%20photography%2C%20cinematic%20lighting&width=1400&height=700&seq=cross-banner-support-v3&orientation=landscape')` }}
              />
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-slate-950/98 via-slate-900/90 to-slate-800/75 z-10" />

              {/* Decorative circle */}
              <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-amber-500/10 z-10" />
              <div className="absolute -right-8 -bottom-16 w-64 h-64 rounded-full bg-amber-400/10 z-10" />

              <div className="relative z-20 px-10 py-16 md:px-20 md:py-20">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

                  {/* Left: Text content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-11 h-11 flex items-center justify-center bg-amber-500 rounded-xl flex-shrink-0 shadow-lg shadow-amber-500/40">
                        <i className="ri-building-4-line text-white text-xl"></i>
                      </div>
                      <span className="text-amber-400 text-sm font-bold tracking-[0.15em] uppercase">
                        開業後の運営もすべてお任せ
                      </span>
                    </div>

                    <h3 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
                      Airbnb運営代行・<br />清掃サービス
                    </h3>

                    <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-xl">
                      開業後の集客・ゲスト対応・清掃まで、<br className="hidden md:block" />
                      すべてプロが代行。オーナー様は<strong className="text-white">ほぼ手放し</strong>で収益を得られます。
                    </p>

                    {/* Feature list */}
                    <div className="grid grid-cols-2 gap-3 max-w-lg">
                      {[
                        { icon: 'ri-global-line', label: '多言語ゲスト対応' },
                        { icon: 'ri-line-chart-line', label: 'ダイナミックプライシング' },
                        { icon: 'ri-brush-line', label: '清掃・リネン交換' },
                        { icon: 'ri-file-chart-line', label: '月次売上レポート' },
                        { icon: 'ri-star-line', label: 'レビュー管理' },
                        { icon: 'ri-customer-service-2-line', label: '24時間ゲスト対応' },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center gap-2.5 bg-white/8 backdrop-blur-sm px-4 py-2.5 rounded-xl border border-white/10">
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                            <i className={`${item.icon} text-amber-400 text-base`}></i>
                          </div>
                          <span className="text-white text-sm font-medium">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Stats + CTA */}
                  <div className="flex flex-col items-stretch gap-5 w-full lg:w-72 flex-shrink-0">
                    {/* Stats cards */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 backdrop-blur-md rounded-2xl px-5 py-5 text-center border border-white/15 hover:bg-white/15 transition-colors">
                        <div className="text-3xl font-extrabold text-amber-400 mb-1">98%</div>
                        <div className="text-xs text-slate-300 leading-tight">顧客<br />満足度</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-md rounded-2xl px-5 py-5 text-center border border-white/15 hover:bg-white/15 transition-colors">
                        <div className="text-3xl font-extrabold text-amber-400 mb-1">東京</div>
                        <div className="text-xs text-slate-300 leading-tight">エリア<br />実績多数</div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-white/10" />

                    {/* CTA Button */}
                    <button
                      onClick={() => navigate('/management')}
                      className="group w-full inline-flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-400 text-white font-bold px-8 py-5 rounded-2xl transition-all duration-200 hover:shadow-[0_8px_40px_rgba(245,158,11,0.5)] hover:-translate-y-1 whitespace-nowrap cursor-pointer text-lg"
                    >
                      <span>運営代行を詳しく見る</span>
                      <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full group-hover:translate-x-1 transition-transform">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </div>
                    </button>

                    <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-shield-check-line text-emerald-400"></i>
                        初回相談無料
                      </span>
                      <span className="w-px h-3 bg-slate-600"></span>
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-emerald-400"></i>
                        東京エリア対応
                      </span>
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom accent bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 via-yellow-300 to-amber-500 z-20" />
            </div>

          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20business%20consultation%20desk%20with%20documents%20and%20pen%2C%20clean%20modern%20office%20environment%2C%20soft%20warm%20lighting%2C%20trust%20and%20expertise%20concept%2C%20minimalist%20Japanese%20business%20aesthetic%2C%20high%20quality%20photography%2C%20neutral%20tones%20with%20amber%20accents&width=1920&height=900&seq=contact-bg-support-v2&orientation=landscape')` }}
        />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600" />
        <div className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-amber-500/10" />
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-amber-500/10" />

        <div className="relative z-10 container mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-4">
              <i className="ri-mail-line text-base"></i>
              Contact
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">お問い合わせ</h2>
            <p className="text-lg text-slate-300">まずはお気軽にご相談ください。初回相談は無料です。</p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-10 items-start">
            {/* Left info */}
            <div className="md:col-span-2 space-y-5">
              <div className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-2xl p-7">
                <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-xl flex-shrink-0">
                    <i className="ri-information-line text-white text-lg"></i>
                  </div>
                  ご相談前に
                </h3>
                <ul className="space-y-4">
                  {[
                    { icon: 'ri-time-line', text: '初回相談は完全無料です' },
                    { icon: 'ri-map-pin-line', text: '東京エリアを中心に対応' },
                    { icon: 'ri-reply-line', text: '2営業日以内にご返信' },
                    { icon: 'ri-shield-check-line', text: '個人情報は厳重に管理します' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-amber-400 text-base`}></i>
                      </div>
                      <span className="text-slate-300 text-sm">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-2xl p-7">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl mb-4">
                  <i className="ri-question-answer-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">こんな方はぜひご相談を</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  {[
                    '民泊を始めたいが何から始めればいいか分からない',
                    '申請手続きが複雑で困っている',
                    '保健所・消防署への対応が不安',
                    '物件選びで迷っている',
                    '副業として民泊を検討している',
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-amber-400 flex-shrink-0 mt-0.5"></i>
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="md:col-span-3 bg-white rounded-2xl shadow-2xl p-8 md:p-10">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-slate-900 rounded-xl">
                  <i className="ri-send-plane-line text-white text-lg"></i>
                </div>
                無料相談フォーム
              </h3>
              <form id="support-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="山田 太郎"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="090-1234-5678"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">
                    ご相談内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="ご相談内容をご記入ください（500文字以内）"
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all resize-none text-sm"
                  ></textarea>
                  <div className="text-right text-xs text-slate-400 mt-1">
                    {formData.message.length}/500文字
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white py-4 rounded-lg font-bold transition-all hover:shadow-lg hover:shadow-amber-500/30 hover:-translate-y-1 disabled:bg-slate-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <i className="ri-mail-send-line text-lg"></i>
                  {isSubmitting ? '送信中...' : '無料相談を申し込む'}
                </button>

                {submitStatus === 'success' && (
                  <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-checkbox-circle-line text-emerald-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span>お問い合わせありがとうございます。2営業日以内にご連絡させていただきます。</span>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span>送信に失敗しました。お手数ですが、もう一度お試しください。</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
